<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import moment from 'moment'
import {
  VAlert,
  VBtn,
  VCard,
  VCardActions,
  VCardText,
  VCardTitle,
  VChip,
  VCol,
  VDialog,
  VIcon,
  VProgressCircular,
  VRow,
} from 'vuetify/lib/components/index.mjs'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import CustomDataTable from '@/components/CustomDataTable.vue'
import { useApiOperi } from '@/composables/useApiOperi'
import {
  OrderDrivingActionTypeEnum,
  OrderStatusEnum,
  OrderStepEnum,
  OutsourceLaundryClassificationStatus,
  PaymentMethodEnum,
} from '@/enums.js'
import CustomDropdownFilter from '@/components/CustomDropdownFilter.vue'
import CustomDatePicker from '@/components/CustomDatePicker.vue'
import DrivingDayBoard from '@/components/orders/DrivingDayBoard.vue'
import MobileOrdersViewToggle from '@/components/orders/MobileOrdersViewToggle.vue'
import { useMobileOrdersView } from '@/composables/useMobileOrdersView'
import { ORDER_ACTION_META } from '@/utils/orderActionMeta'
import { PAYMENT_STATUS_LABELS, PAYMENT_STATUS_UNPAID } from '@/utils/paymentLabels'
import { isFreeOrder } from '@/utils/freeOrder'
import { orderFamilyMarkerHtml } from '@/utils/orderFamily'
import { $apiOperi } from '@/utils/apiOperi'
import { isCurrentUserAdmin } from '@/utils/portalRoles'

/**
 * Shared with drivers, who get the same table scoped by the backend to the orders
 * currently assigned to them. What differs is the controls: planning the day's driving,
 * looking any order up by code, and resolving an on-hold order are the admin's job, so a
 * driver is not shown buttons that would be refused.
 */
const isAdmin = isCurrentUserAdmin()

definePage({
  meta: {
    action: 'read',
    subject: 'Orders',
  },
})

const { smAndDown: isMobile } = useDisplay()
const filtersExpanded = ref(!isMobile.value)
const { t } = useI18n()

// One label per OrderDrivingActionTypeEnum value, in the enum's order.
const nextActionLabels = Object.values(OrderDrivingActionTypeEnum).map(type =>
  t(type.endsWith('PICKUP') ? 'orders.nextAction.pickup' : 'orders.nextAction.delivery'))

const nextActionTooltips = Object.values(OrderDrivingActionTypeEnum).map(type =>
  t(ORDER_ACTION_META[type].labelKey))

const drivingView = ref(false)
const mobileOrdersView = useMobileOrdersView()

// Type for the API responses using direct interface instead of external
interface OrdersResponse {
  orders: OrderResponse[]
}

const { data: ordersData } = await useApiOperi<OrdersResponse>('admin/orders')
const currentTab = ref(0)
const router = useRouter()

// Server-side lookup of the public order code, for the case the table's client-side search cannot
// cover: an order that is not among the rows currently loaded.
const lookupCode = ref('')
const lookupLoading = ref(false)
const lookupError = ref('')

const findOrderByCode = async () => {
  const code = lookupCode.value.trim().toUpperCase()
  if (!code)
    return

  lookupLoading.value = true
  lookupError.value = ''

  try {
    const { data, statusCode } = await useApiOperi<{ id?: number }>(
      `admin/order/by-code/${encodeURIComponent(code)}`,
    ).json()

    const orderId = data.value?.id
    if (orderId) { router.push(`/apps/ecommerce/order/details/${orderId}`) }
    else {
      lookupError.value
        = statusCode.value === 404
          ? `No order with code ${code}`
          : 'Lookup failed'
    }
  }
  catch {
    lookupError.value = 'Lookup failed'
  }
  finally {
    lookupLoading.value = false
  }
}

const orders = ref<OrderResponse[]>(ordersData.value?.orders || [])

watch(
  ordersData,
  val => {
    orders.value = val?.orders || []
  },
  { immediate: true, deep: true },
)

interface TimeWindow {
  startTime?: string
  endTime?: string
}

interface LngLat {
  lng: number
  lat: number
}

// Updated OrderDrivingActionAddress interface to match new fields
interface OrderDrivingActionAddress {
  city?: string
  countryCode?: string
  geoLocation?: { lat?: number; lng?: number } | null
  instructions?: string
  streetName?: string
  streetNumber?: string
  suiteNumber?: string
  zip?: string
}

interface OrderDrivingAction {
  type?: OrderDrivingActionTypeEnum
  dueDate?: string
  contactName?: string
  contactPhone?: string
  preferredTimeWindows?: TimeWindow[]
  address?: OrderDrivingActionAddress
  dueIn?: number
  inProgress?: boolean
}

interface Customer {
  id: number
  name: string
  email: string
  phone?: string
}

interface OutsourceCompany {
  id: number
  name: string
  phone?: string
}

interface Order {
  id: number
  customer: Customer
  customerComment?: string | null
  outsourceCompany?: OutsourceCompany
  outsourceOrderId?: string | null
  outsourceLaundryStatus?: string
  currentStep?: OrderStepEnum
  orderStatus?: OrderStatusEnum
  nextDrivingAction?: OrderDrivingAction | null
  nextAction?: string
  finalTotalPrice?: number
  currentStepLastUpdate?: string
  isQuickDeliveryPreferred?: boolean
  hasOutsourceUpdate?: boolean
}

type OrderResponse = Order

type ProcessedOrder = Omit<Order, 'nextDrivingAction'> & {
  nextDrivingAction: (OrderDrivingAction & { dueIn: number }) | null
}

const getDaysUntilDue = (dueDate: string) => {
  const today = moment().startOf('day')
  const due = moment(dueDate).startOf('day')

  return due.diff(today, 'days')
}

const processedOrders = computed(() => {
  return orders.value.map(
    (order: Order): ProcessedOrder => ({
      ...order,
      nextDrivingAction: order.nextDrivingAction
        ? {
            ...order.nextDrivingAction,
            dueIn: getDaysUntilDue(order.nextDrivingAction.dueDate || ''),
          }
        : null,
    }),
  )
})

const activeOrders = computed(() =>
  processedOrders.value.filter(
    (order: ProcessedOrder) => order.orderStatus === OrderStatusEnum.ACTIVE,
  ),
)

const completedOrders = computed(() =>
  processedOrders.value.filter(
    (order: ProcessedOrder) => order.orderStatus === OrderStatusEnum.COMPLETED,
  ),
)

const canceledOrders = computed(() =>
  processedOrders.value.filter(
    (order: ProcessedOrder) => order.orderStatus === OrderStatusEnum.CANCELED,
  ),
)

// --- FIX: On hold table bug ---
// The issue is likely that the CustomDataTable expects a unique :key for each row, and/or the table is not updating because the :items array is not a new reference.
// To ensure reactivity, we provide a computed that returns a new array reference for onHoldOrders.
const onHoldOrders = computed(() =>
  processedOrders.value.filter(
    (order: ProcessedOrder) => order.orderStatus === OrderStatusEnum.ON_HOLD,
  ),
)

const activeOrdersNumber = computed(() => activeOrders.value.length)
const completedOrdersNumber = computed(() => completedOrders.value.length)
const canceledOrdersNumber = computed(() => canceledOrders.value.length)
const onHoldOrdersNumber = computed(() => onHoldOrders.value.length)
const totalOrdersNumber = computed(() => orders.value.length)

const completedTotalPrice = computed(() =>
  completedOrders.value.reduce(
    (sum: number, order: ProcessedOrder) => sum + (order.finalTotalPrice || 0),
    0,
  ),
)

const iconActive = 'tabler-progress'
const iconCompleted = 'tabler-check'
const iconCanceled = 'tabler-navigation-cancel'
const iconTotal = 'tabler-checks'
const iconMoney = 'tabler-currency-dollar'

const isOutsourceClassificationLate = (item: any) => {
  const isCurrentStepLaundryClassification
    = item.step === OrderStepEnum.LAUNDRY_CLASSIFICATION

  const daysUntil = item.nextDrivingAction?.dueIn
  const isDueSoon = daysUntil !== undefined && daysUntil <= 1

  const hasNoOutsourceUpdate = item.hasOutsourceUpdate == null // checks for null or undefined

  return (
    isCurrentStepLaundryClassification && isDueSoon && hasNoOutsourceUpdate
  )
}

const isOutsourceClassificationDraft = (item: any) => {
  const isCurrentStepLaundryClassification
    = item.step === OrderStepEnum.LAUNDRY_CLASSIFICATION

  const hasOutsourceUpdate = item.hasOutsourceUpdate === true

  return isCurrentStepLaundryClassification && hasOutsourceUpdate
}

const isOutsourceClassificationSync = (item: any) => {
  const isLaundryProcessing = item.step === OrderStepEnum.LAUNDRY_PROCESSING
  const hasNoOutsourceUpdate = item.hasOutsourceUpdate === false

  return isLaundryProcessing && hasNoOutsourceUpdate
}

const isOutsourceLateToComplete = (item: any) => {
  const outsourceOrderItemsInSync = isOutsourceClassificationSync(item)

  const daysUntil = item.nextDrivingAction?.dueIn
  const isDueSoon = daysUntil !== undefined && daysUntil <= 1

  return outsourceOrderItemsInSync && isDueSoon
}

// Base columns that are common across all tabs
const baseColumns = [
  {
    // The identifier the customer sees in the app. Searchable, so support can look up
    // an order from the code a customer reads out.
    title: 'Code',
    key: 'code',
    type: 'string' as const,
    sortable: true,

    // The id only stands in for an order that has no code, so every row still opens.
    render: (item: any) => `#${item.code || item.id}${orderFamilyMarkerHtml(item)}`,
    href: (item: any) => `/apps/ecommerce/order/details/${item.id}`,
  },
  {
    title: 'Name',
    key: 'customer.name',
    type: 'string' as const,
    sortable: true,
    render: (item: any) => item.customer?.name ?? '',
  },
  {
    title: 'Type',
    key: 'isQuickDeliveryPreferred',
    type: 'enum' as const,
    sortable: true,
    enumOptions: ['QUICK', 'REGULAR'],
    enumColors: ['#d97706', '#1a53ff'], // Amber for Quick, Blue for Regular
    enumIcons: ['tabler-bolt', 'tabler-truck'],
    enumTooltips: [t('orders.deliveryType.quick'), t('orders.deliveryType.regular')],
    enumIconOnly: true,
    map: (item: any) => (item.isQuickDeliveryPreferred ? 'QUICK' : 'REGULAR'),
  },
  {
    title: 'Payment',
    key: 'paymentStatus',
    type: 'enum' as const,
    sortable: true,

    // UNPAID outranks the raw status: an order delivered with the money never
    // collected is the one staff must act on, whatever its payment row says.
    enumOptions: [
      'FREE',
      'UNPAID',
      'PAID',
      'RESERVED',
      'SCHEDULED',
      'PENDING',
      'FAILED',
      'CANCELED',
      'REFUNDED',
      PaymentMethodEnum.CASH_ON_DELIVERY,
      PaymentMethodEnum.CARD_ON_DELIVERY,
      PaymentMethodEnum.ONLINE_CARD,
    ],
    enumColors: [
      '#16a34a',
      '#dc2626',
      '#16a34a',
      '#0891b2',
      '#d97706',
      '#1a53ff',
      '#dc2626',
      '#6b7280',
      '#7c3aed',
      '#6b7280',
      '#6b7280',
      '#6b7280',
    ],
    enumIcons: [
      'tabler-gift',
      'tabler-alert-triangle',
      'tabler-circle-check',
      'tabler-lock',
      'tabler-clock',
      'tabler-loader',
      'tabler-circle-x',
      'tabler-ban',
      'tabler-receipt-refund',
      'tabler-cash',
      'tabler-credit-card',
      'tabler-world',
    ],

    // Before any payment exists the status is empty, so the column says how the order will
    // be paid instead, as an icon with the method in its tooltip.
    enumTooltips: [
      ...Array.from({ length: 9 }, () => ''),
      t('orders.paymentMethod.CASH_ON_DELIVERY'),
      t('orders.paymentMethod.CARD_ON_DELIVERY'),
      t('orders.paymentMethod.ONLINE_CARD'),
    ],
    enumIconOnly: [
      PaymentMethodEnum.CASH_ON_DELIVERY,
      PaymentMethodEnum.CARD_ON_DELIVERY,
      PaymentMethodEnum.ONLINE_CARD,
    ],
    map: (item: any) => {
      // UNPAID outranks the raw status, then the shared labels in
      // utils/paymentLabels so this column, the order panel and the settings
      // screen all name a state the same way.
      // A free order has nothing to pay, so it is never UNPAID.
      if (isFreeOrder(item.finalTotalPrice, item.paymentStatus))
        return 'FREE'
      if (item.deliveredUnpaid)
        return PAYMENT_STATUS_UNPAID

      return PAYMENT_STATUS_LABELS[item.paymentStatus] ?? item.paymentMethod ?? ''
    },
    rowTooltip: (item: any) => item.paymentLastError || '',
  },
]

// Columns for completed orders
const completedColumns = [
  ...baseColumns,
  {
    title: 'Completed At',
    key: 'currentStepLastUpdate',
    type: 'datetime' as const,
    sortable: true,
    render: (item: any) => {
      const date = moment(item.currentStepLastUpdate)

      return `${date.format('DD MMM YYYY HH:mm')}`
    },
  },
  {
    title: 'Price',
    key: 'finalTotalPrice',
    type: 'number' as const,
    sortable: true,
    render: (item: any) =>
      item.finalTotalPrice ? `<b>${item.finalTotalPrice} RSD</b>` : '',
  },
]

// Columns for canceled orders
const canceledColumns = [
  ...baseColumns,
  {
    title: 'Canceled At',
    key: 'currentStepLastUpdate',
    type: 'datetime' as const,
    sortable: true,
    render: (item: any) => {
      const date = moment(item.currentStepLastUpdate)

      return `${date.format('DD MMM YYYY HH:mm')}`
    },
  },
]

// Columns for active orders
const activeColumns = [
  ...baseColumns,
  {
    title: 'Next Action',
    key: 'nextDrivingAction.type',
    type: 'enum' as const,
    sortable: true,
    enumOptions: Object.values(OrderDrivingActionTypeEnum),

    // Pickup or delivery in words. The icon and colour say whether it is the customer's
    // door or the laundry, and the tooltip names it in full. The filter keeps all four.
    enumLabels: nextActionLabels,
    enumTooltips: nextActionTooltips,
    enumColors: [
      '#1a53ff', // CUSTOMER_PICKUP - stronger blue
      '#0033cc', // CUSTOMER_DELIVERY - darker blue
      '#d97706', // STORE_PICKUP - strong amber
      '#b45309', // STORE_DELIVERY - darker amber
    ],
    enumIcons: [
      'tabler-user-up', // CUSTOMER_PICKUP
      'tabler-user-down', // CUSTOMER_DELIVERY
      'tabler-building-store', // STORE_PICKUP
      'tabler-truck-delivery', // STORE_DELIVERY
    ],
  },
  {
    title: 'Due Date',
    key: 'nextDrivingAction.dueDate',
    type: 'datetime' as const,
    sortable: true,
    render: (item: any) => {
      const date = moment(item.nextDrivingAction?.dueDate)

      return date.isValid() ? date.format('DD MMM YYYY') : ''
    },
  },
  {
    title: 'Due In',
    key: 'nextDrivingAction.dueIn',
    type: 'number' as const,
    sortable: true,
    render: (item: any) => {
      const daysUntil = item.nextDrivingAction?.dueIn
      if (daysUntil === undefined)
        return '-'

      let daysDisplay
      if (daysUntil < 0) {
        const daysText = Math.abs(daysUntil) === 1 ? 'day' : 'days'

        daysDisplay = `<span style="font-size: 1.2em; margin-right: 2px;">-</span>${Math.abs(daysUntil)} ${daysText}`

        return `<span style="color: rgb(var(--v-theme-error));"><b>${daysDisplay} ●</b></span>`
      }
      else if (daysUntil <= 1) {
        const daysText = daysUntil === 1 ? 'day' : 'days'

        daysDisplay = `${daysUntil} ${daysText}`

        return `<span style="color: rgb(var(--v-theme-warning));"><b>${daysDisplay} ▲</b></span>`
      }
      else {
        const daysText = daysUntil === 1 ? 'day' : 'days'

        daysDisplay = `${daysUntil} ${daysText}`

        return `<b>${daysDisplay}</b>`
      }
    },
  },
  {
    title: 'Driving In Progress',
    key: 'nextDrivingAction.inProgress',
    type: 'boolean' as const,
    sortable: true,
    map: (item: any) => !!item.nextDrivingAction?.inProgress,
  },
  {
    title: 'Outsource',
    key: 'hasOutsourceUpdate',
    type: 'enum' as const,
    sortable: false,
    enumOptions: [
      ...Object.values(OutsourceLaundryClassificationStatus),
      'LATE',
      'LATE TO COMPLETE',
    ],
    enumColors: [
      '#991b1b', // Dark red for draft
      '#0694a2', // Teal for sync
      '#d97706', // Amber for late
      '#b45309', // Darker amber for late to complete
    ],
    enumIcons: [
      'tabler-pencil', // Pencil for draft
      'tabler-refresh', // Refresh for sync
      'tabler-alert-triangle', // Warning for late
      'tabler-alert-circle', // Alert circle for late to complete
    ],
    enumTooltips: [
      'Confirm order items classification!',
      '',
      'Contact outsource company to submit order items!',
      'Contact Outsource company to check why Order is not completed yet in case they forgot it',
    ],
    map: (item: any) => {
      const isClassificationLate = isOutsourceClassificationLate(item)
      const isDraft = isOutsourceClassificationDraft(item)
      const isSync = isOutsourceClassificationSync(item)
      const isLateToComplete = isOutsourceLateToComplete(item)

      if (isClassificationLate)
        return 'LATE'

      if (isDraft)
        return OutsourceLaundryClassificationStatus.draft

      if (isLateToComplete)
        return 'LATE TO COMPLETE'

      if (isSync)
        return OutsourceLaundryClassificationStatus.sync

      return null
    },

    // Links into the order-items approval page, which is admin-only - a driver following
    // this cell was sent there and bounced to not-authorized. Approving what the laundry
    // charged for is the admin's job; the column still shows the outsource state, it just
    // stops being a doorway.
    href: (item: any) => {
      if (isAdmin && item.hasOutsourceUpdate)
        return `/apps/ecommerce/order/approval/${item.id}`

      return ''
    },
  },
  {
    title: 'Price',
    key: 'finalTotalPrice',
    type: 'number' as const,
    sortable: true,
    render: (item: any) =>
      item.finalTotalPrice ? `<b>${item.finalTotalPrice} RSD</b>` : '',
  },
]

// Columns for on-hold orders
const onHoldColumns = [
  ...baseColumns,
  {
    title: 'Next Action',
    key: 'nextDrivingAction.type',
    type: 'enum',
    sortable: true,
    enumOptions: Object.values(OrderDrivingActionTypeEnum),

    // Pickup or delivery in words. The icon and colour say whether it is the customer's
    // door or the laundry, and the tooltip names it in full. The filter keeps all four.
    enumLabels: nextActionLabels,
    enumTooltips: nextActionTooltips,
    enumColors: [
      '#1a53ff', // CUSTOMER_PICKUP - stronger blue
      '#0033cc', // CUSTOMER_DELIVERY - darker blue
      '#d97706', // STORE_PICKUP - strong amber
      '#b45309', // STORE_DELIVERY - darker amber
    ],
    enumIcons: [
      'tabler-user-up', // CUSTOMER_PICKUP
      'tabler-user-down', // CUSTOMER_DELIVERY
      'tabler-building-store', // STORE_PICKUP
      'tabler-truck-delivery', // STORE_DELIVERY
    ],
  },
  {
    title: 'Due Date',
    key: 'nextDrivingAction.dueDate',
    type: 'datetime' as const,
    sortable: true,
    render: (item: any) => {
      const date = moment(item.nextDrivingAction?.dueDate)

      return date.isValid() ? date.format('DD MMM YYYY') : ''
    },
  },
  {
    title: 'Due In',
    key: 'nextDrivingAction.dueIn',
    type: 'number' as const,
    sortable: true,
    render: (item: any) => {
      const daysUntil = item.nextDrivingAction?.dueIn
      if (daysUntil === undefined)
        return '-'

      let daysDisplay
      if (daysUntil < 0) {
        const daysText = Math.abs(daysUntil) === 1 ? 'day' : 'days'

        daysDisplay = `<span style="font-size: 1.2em; margin-right: 2px;">-</span>${Math.abs(daysUntil)} ${daysText}`

        return `<span style="color: rgb(var(--v-theme-error));"><b>${daysDisplay} ●</b></span>`
      }
      else if (daysUntil <= 1) {
        const daysText = daysUntil === 1 ? 'day' : 'days'

        daysDisplay = `${daysUntil} ${daysText}`

        return `<span style="color: rgb(var(--v-theme-warning));"><b>${daysDisplay} ▲</b></span>`
      }
      else {
        const daysText = daysUntil === 1 ? 'day' : 'days'

        daysDisplay = `${daysUntil} ${daysText}`

        return `<b>${daysDisplay}</b>`
      }
    },
  },
  {
    title: 'Driving In Progress',
    key: 'nextDrivingAction.inProgress',
    type: 'boolean' as const,
    sortable: true,
    map: (item: any) => !!item.nextDrivingAction?.inProgress,
  },
]

// Plan CTA — destination + date selectors push to /apps/ecommerce/order/plan
const planningDestinationOptions = ['CUSTOMER', 'STORE']
const selectedPlanningDestination = ref<string | null>(null)
const selectedPlanningDate = ref<Date | null>(null)

const onPlanCta = () => {
  const query: Record<string, string> = {}
  if (selectedPlanningDestination.value)
    query.destinations = selectedPlanningDestination.value
  if (selectedPlanningDate.value)
    query.date = moment(selectedPlanningDate.value).format('YYYY-MM-DD')

  router.push({ path: '/apps/ecommerce/order/plan', query })
}

// Function to get action display name (used by Resolve dialog below)
const getActionDisplayName = (
  actionType: OrderDrivingActionTypeEnum | '',
): string => {
  if (!actionType)
    return ''

  switch (actionType) {
    case OrderDrivingActionTypeEnum.CUSTOMER_PICKUP:
      return 'Customer Pickup'
  case OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY:
      return 'Customer Delivery'
  case OrderDrivingActionTypeEnum.STORE_PICKUP:
      return 'Store Pickup'
  case OrderDrivingActionTypeEnum.STORE_DELIVERY:
      return 'Store Delivery'
  default:
      return ''
  }
}

// (planning CSV export logic moved to /apps/ecommerce/order/plan)
// Add code for resolve dialog
const isResolveDialogOpen = ref(false)
const selectedOrder = ref<ProcessedOrder | null>(null)
const pickupDate = ref<Date | null>(null)
const deliveryDate = ref<Date | null>(null)
const isResolving = ref(false)

const resolveMessage = ref<{ type: 'success' | 'error'; text: string } | null>(
  null,
)

const pickupTimeWindows = ref<TimeWindow[]>([])
const deliveryTimeWindows = ref<TimeWindow[]>([])
const selectedPickupTimeWindows = ref<TimeWindow[]>([])
const selectedDeliveryTimeWindows = ref<TimeWindow[]>([])

// New state for checkboxes and due date
const modifyPickup = ref(false)
const modifyDelivery = ref(false)
const nextDrivingActionDueDate = ref<Date | null>(null)

// Add global success message that will appear above the table
const globalSuccessMessage = ref<string | null>(null)
const isLoadingOrders = ref<boolean>(false)

// Computed helpers for dialog logic
const nextDrivingActionType = computed(
  () => selectedOrder.value?.nextDrivingAction?.type || null,
)

const isStoreAction = computed(() => {
  return (
    nextDrivingActionType.value === OrderDrivingActionTypeEnum.STORE_PICKUP
    || nextDrivingActionType.value === OrderDrivingActionTypeEnum.STORE_DELIVERY
  )
})

const isCustomerPickup = computed(() => {
  return (
    nextDrivingActionType.value === OrderDrivingActionTypeEnum.CUSTOMER_PICKUP
  )
})

const isCustomerDelivery = computed(() => {
  return (
    nextDrivingActionType.value === OrderDrivingActionTypeEnum.CUSTOMER_DELIVERY
  )
})

// Logic for which checkboxes to show (can be expanded if needed)
const canModifyPickup = computed(() => {
  // Cannot modify customer pickup if STORE is action
  if (isStoreAction.value)
    return false

  return isCustomerPickup.value
}) // adjust if needed

const canModifyDelivery = computed(() => {
  return true
}) // always allow delivery modification for now

// Function to fetch available dates settings
const fetchAvailableDatesSettings = async () => {
  try {
    const { data: availableDatesSettings } = await useApiOperi<{
      pickupTimeWindows?: TimeWindow[]
      deliveryTimeWindows?: TimeWindow[]
    }>('/available-date-rules/get-active')

    if (availableDatesSettings.value) {
      pickupTimeWindows.value
        = availableDatesSettings.value.pickupTimeWindows || []
      deliveryTimeWindows.value
        = availableDatesSettings.value.deliveryTimeWindows || []
    }
  }
  catch (error) {
    console.error('Failed to fetch available dates settings:', error)
    pickupTimeWindows.value = []
    deliveryTimeWindows.value = []
  }
}

// Function to show resolve dialog
const showResolveDialog = async (order: ProcessedOrder) => {
  selectedOrder.value = { ...order } // Create a copy to avoid reference issues
  // Always clear date fields on open
  pickupDate.value = null
  deliveryDate.value = null
  nextDrivingActionDueDate.value = null
  resolveMessage.value = null
  isResolveDialogOpen.value = true

  // Fetch available time windows immediately when dialog opens
  await fetchAvailableDatesSettings()

  // Preselect all time windows by default
  selectedPickupTimeWindows.value = [...pickupTimeWindows.value]
  selectedDeliveryTimeWindows.value = [...deliveryTimeWindows.value]

  // Set checkbox defaults
  if (isStoreAction.value) {
    modifyPickup.value = false
    modifyDelivery.value = false
  }
  else if (isCustomerPickup.value) {
    modifyPickup.value = true
    modifyDelivery.value = false
  }
  else if (isCustomerDelivery.value) {
    if (canModifyPickup.value) {
      modifyPickup.value = false
      modifyDelivery.value = false
    }
    else {
      modifyPickup.value = false
      modifyDelivery.value = true
    }
  }
  console.log('Dialog opened for order:', selectedOrder.value.id)
}

// Function to close resolve dialog
const closeResolveDialog = () => {
  resolveMessage.value = null // Clear error before closing dialog to prevent flash
  isResolveDialogOpen.value = false
  selectedOrder.value = null
  pickupDate.value = null
  deliveryDate.value = null
  selectedPickupTimeWindows.value = []
  selectedDeliveryTimeWindows.value = []
}

// Computed to determine if resolve button should be enabled
const isResolveButtonDisabled = computed(() => {
  if (!selectedOrder.value)
    return true
  if (isResolving.value)
    return true

  const today = new Date()

  today.setHours(0, 0, 0, 0)

  // Helper to check if a date is today or in the future
  const isValidDate = (date: unknown) => {
    if (!date)
      return false
    const d = new Date(date as any)

    d.setHours(0, 0, 0, 0)

    return d >= today
  }

  if (isStoreAction.value) {
    // For STORE, only due date is required
    if (
      !nextDrivingActionDueDate.value
      || !isValidDate(nextDrivingActionDueDate.value)
    )
      return true
    if (canModifyPickup.value && modifyPickup.value) {
      if (
        !pickupDate.value
        || !isValidDate(pickupDate.value)
        || selectedPickupTimeWindows.value.length === 0
      )
        return true
    }

    if (canModifyDelivery.value && modifyDelivery.value) {
      if (
        !deliveryDate.value
        || !isValidDate(deliveryDate.value)
        || selectedDeliveryTimeWindows.value.length === 0
      )
        return true
    }

    // If both are checked, delivery must be after pickup
    if (
      modifyPickup.value
      && modifyDelivery.value
      && pickupDate.value
      && deliveryDate.value
    ) {
      if (new Date(deliveryDate.value) <= new Date(pickupDate.value))
        return true
    }

    return false
  }

  if (isCustomerPickup.value) {
    // Pickup is mandatory
    if (
      !pickupDate.value
      || !isValidDate(pickupDate.value)
      || selectedPickupTimeWindows.value.length === 0
    )
      return true
    if (canModifyDelivery.value && modifyDelivery.value) {
      if (
        !deliveryDate.value
        || !isValidDate(deliveryDate.value)
        || selectedDeliveryTimeWindows.value.length === 0
      )
        return true

      // If both are filled, delivery must be after pickup
      if (
        pickupDate.value
        && deliveryDate.value
        && new Date(deliveryDate.value) <= new Date(pickupDate.value)
      )
        return true
    }

    return false
  }

  if (isCustomerDelivery.value) {
    // Delivery is mandatory
    if (
      !deliveryDate.value
      || !isValidDate(deliveryDate.value)
      || selectedDeliveryTimeWindows.value.length === 0
    )
      return true
    if (canModifyPickup.value && modifyPickup.value) {
      if (
        !pickupDate.value
        || !isValidDate(pickupDate.value)
        || selectedPickupTimeWindows.value.length === 0
      )
        return true

      // If both are filled, delivery must be after pickup
      if (
        pickupDate.value
        && deliveryDate.value
        && new Date(deliveryDate.value) <= new Date(pickupDate.value)
      )
        return true
    }

    return false
  }

  return true
})

// For all date pickers, set :min-date="minDateForPicker" where minDateForPicker is today
const minDateForPicker = computed(() => {
  const today = new Date()

  today.setHours(0, 0, 0, 0)

  return today
})

// Format time window for display
const formatTimeWindow = (window: TimeWindow): string => {
  if (!window.startTime || !window.endTime)
    return ''
  const start = window.startTime.substring(0, 5)
  const end = window.endTime.substring(0, 5)

  return `${start} - ${end}`
}

// Function to toggle selection of a time window
const toggleTimeWindow = (window: TimeWindow, isPickup: boolean) => {
  const targetArray = isPickup
    ? selectedPickupTimeWindows
    : selectedDeliveryTimeWindows

  // Check if window is already selected
  const existingIndex = targetArray.value.findIndex(
    w => w.startTime === window.startTime && w.endTime === window.endTime,
  )

  if (existingIndex >= 0) {
    // Remove if already selected
    targetArray.value.splice(existingIndex, 1)
  }
  else {
    // Add if not selected
    targetArray.value.push({ ...window })
  }
}

// Check if time window is selected
const isTimeWindowSelected = (
  window: TimeWindow,
  isPickup: boolean,
): boolean => {
  const targetArray = isPickup
    ? selectedPickupTimeWindows
    : selectedDeliveryTimeWindows

  return targetArray.value.some(
    w => w.startTime === window.startTime && w.endTime === window.endTime,
  )
}

// Function to fetch orders and refresh the state
const refreshOrders = async () => {
  isLoadingOrders.value = true
  try {
    // Force a direct API call to get fresh data
    const { data: freshOrdersData }
      = await useApiOperi<OrdersResponse>('admin/orders')

    if (freshOrdersData.value && freshOrdersData.value.orders) {
      ordersData.value = freshOrdersData.value
      await nextTick()
      orders.value = freshOrdersData.value.orders

      return true
    }

    return false
  }
  catch (error) {
    console.error('Error refreshing orders:', error)

    return false
  }
  finally {
    isLoadingOrders.value = false
  }
}

// Function to resolve the order
const resolveOrder = async () => {
  if (!selectedOrder.value) {
    resolveMessage.value = {
      type: 'error',
      text: 'Error: No order selected. Please try again.',
    }

    return
  }

  if (isResolveButtonDisabled.value)
    return

  const orderId = selectedOrder.value.id

  isResolving.value = true
  resolveMessage.value = null

  try {
    // Prepare request data according to new rules
    const type = selectedOrder.value.nextDrivingAction?.type
    const requestData: any = { orderId }

    if (isStoreAction.value) {
      requestData.nextDrivingActionDate = nextDrivingActionDueDate.value
        ? moment(nextDrivingActionDueDate.value).format('YYYY-MM-DD')
        : null
      requestData.nextDrivingActionTimeWindows = null
      requestData.newPickupDate = null
      requestData.pickupTimeWindows = null
      requestData.newDeliveryDate = null
      requestData.deliveryTimeWindows = null
      if (canModifyPickup.value && modifyPickup.value) {
        requestData.newPickupDate = pickupDate.value
          ? moment(pickupDate.value).format('YYYY-MM-DD')
          : null
        requestData.pickupTimeWindows
          = selectedPickupTimeWindows.value.length > 0
            ? selectedPickupTimeWindows.value
            : null
      }
      if (canModifyDelivery.value && modifyDelivery.value) {
        requestData.newDeliveryDate = deliveryDate.value
          ? moment(deliveryDate.value).format('YYYY-MM-DD')
          : null
        requestData.deliveryTimeWindows
          = selectedDeliveryTimeWindows.value.length > 0
            ? selectedDeliveryTimeWindows.value
            : null
      }
    }
    else if (isCustomerPickup.value) {
      requestData.nextDrivingActionDate = pickupDate.value
        ? moment(pickupDate.value).format('YYYY-MM-DD')
        : null
      requestData.nextDrivingActionTimeWindows
        = selectedPickupTimeWindows.value.length > 0
          ? selectedPickupTimeWindows.value
          : null
      requestData.newPickupDate = pickupDate.value
        ? moment(pickupDate.value).format('YYYY-MM-DD')
        : null
      requestData.pickupTimeWindows
        = selectedPickupTimeWindows.value.length > 0
          ? selectedPickupTimeWindows.value
          : null
      if (canModifyDelivery.value && modifyDelivery.value) {
        requestData.newDeliveryDate = deliveryDate.value
          ? moment(deliveryDate.value).format('YYYY-MM-DD')
          : null
        requestData.deliveryTimeWindows
          = selectedDeliveryTimeWindows.value.length > 0
            ? selectedDeliveryTimeWindows.value
            : null
      }
      else {
        requestData.newDeliveryDate = null
        requestData.deliveryTimeWindows = null
      }
    }
    else if (isCustomerDelivery.value) {
      requestData.nextDrivingActionDate = deliveryDate.value
        ? moment(deliveryDate.value).format('YYYY-MM-DD')
        : null
      requestData.nextDrivingActionTimeWindows
        = selectedDeliveryTimeWindows.value.length > 0
          ? selectedDeliveryTimeWindows.value
          : null
      requestData.newDeliveryDate = deliveryDate.value
        ? moment(deliveryDate.value).format('YYYY-MM-DD')
        : null
      requestData.deliveryTimeWindows
        = selectedDeliveryTimeWindows.value.length > 0
          ? selectedDeliveryTimeWindows.value
          : null
      if (canModifyPickup.value && modifyPickup.value) {
        requestData.newPickupDate = pickupDate.value
          ? moment(pickupDate.value).format('YYYY-MM-DD')
          : null
        requestData.pickupTimeWindows
          = selectedPickupTimeWindows.value.length > 0
            ? selectedPickupTimeWindows.value
            : null
      }
      else {
        requestData.newPickupDate = null
        requestData.pickupTimeWindows = null
      }
    }

    await $apiOperi('admin/order/resolve-on-hold', {
      method: 'PUT',
      body: requestData,
    })

    closeResolveDialog()
    globalSuccessMessage.value = `Order #${orderId} has been successfully resolved`
    await refreshOrders()
    setTimeout(() => {
      globalSuccessMessage.value = null
    }, 5000)
  }
  catch (error) {
    resolveMessage.value = {
      type: 'error',
      text: `Failed to resolve order: ${error instanceof Error ? error.message : 'Unknown error'}`,
    }
  }
  finally {
    isResolving.value = false
  }
}

// Original function to fetch orders - keep this for other parts of the app
const fetchOrders = async () => {
  try {
    const { data: fetchedOrdersData }
      = await useApiOperi<OrdersResponse>('admin/orders')

    if (fetchedOrdersData.value && fetchedOrdersData.value.orders)
      return fetchedOrdersData.value.orders

    return []
  }
  catch (error) {
    return []
  }
}

// Add computed for minimum delivery date
const minDeliveryDate = computed(() => {
  if (!pickupDate.value)
    return null
  const minDate = new Date(pickupDate.value)

  minDate.setDate(minDate.getDate() + 1)

  return minDate
})

// Add computed for minimum pickup date (today)
const minPickupDate = computed(() => {
  const today = new Date()

  today.setHours(0, 0, 0, 0)

  return today
})
</script>

<template>
  <div>
    <VCard class="mb-6">
      <VCardText class="py-2">
        <VRow
          class="d-flex align-center"
          no-gutters
        >
          <VCol
            cols="auto"
            class="pe-4"
          >
            <div class="d-flex align-center">
              <VAvatar
                variant="tonal"
                rounded
                size="32"
                color="primary"
                class="me-2"
              >
                <VIcon
                  :icon="iconActive"
                  size="20"
                />
              </VAvatar>
              <div>
                <div class="text-h6">
                  {{ activeOrdersNumber }}
                </div>
                <div class="text-body-2">
                  Active Orders
                </div>
              </div>
            </div>
          </VCol>

          <VDivider
            vertical
            class="mx-2"
          />

          <VCol
            cols="auto"
            class="pe-4"
          >
            <div class="d-flex align-center">
              <VAvatar
                variant="tonal"
                rounded
                size="32"
                color="grey"
                class="me-2"
              >
                <span class="text-grey-darken-2 font-weight-medium text-xs">⏱️</span>
              </VAvatar>
              <div>
                <div class="text-h6">
                  {{ onHoldOrdersNumber }}
                </div>
                <div class="text-body-2">
                  On Hold
                </div>
              </div>
            </div>
          </VCol>

          <VDivider
            v-if="isAdmin"
            vertical
            class="mx-2"
          />

          <VCol
            v-if="isAdmin"
            cols="auto"
            class="pe-4"
          >
            <div class="d-flex align-center">
              <VAvatar
                variant="tonal"
                rounded
                size="32"
                color="success"
                class="me-2"
              >
                <span class="text-success-darken-1 font-weight-medium text-xs">✓</span>
              </VAvatar>
              <div>
                <div class="text-h6">
                  {{ completedOrdersNumber }}
                </div>
                <div class="text-body-2">
                  Completed Orders
                </div>
              </div>
            </div>
          </VCol>

          <VDivider
            v-if="isAdmin"
            vertical
            class="mx-2"
          />

          <VCol
            v-if="isAdmin"
            cols="auto"
          >
            <div class="d-flex align-center">
              <VAvatar
                variant="tonal"
                rounded
                size="32"
                color="error"
                class="me-2"
              >
                <span class="text-error-darken-1 font-weight-medium text-xs">X</span>
              </VAvatar>
              <div>
                <div class="text-h6">
                  {{ canceledOrdersNumber }}
                </div>
                <div class="text-body-2">
                  Canceled Orders
                </div>
              </div>
            </div>
          </VCol>

          <VDivider
            v-if="isAdmin"
            vertical
            class="mx-2"
          />

          <VCol
            v-if="isAdmin"
            cols="auto"
            class="pe-4"
          >
            <div class="d-flex align-center">
              <VAvatar
                variant="tonal"
                rounded
                size="32"
                color="success"
                class="me-2"
              >
                <VIcon
                  :icon="iconMoney"
                  size="20"
                />
              </VAvatar>
              <div>
                <div class="text-h6">
                  {{ completedTotalPrice }} RSD
                </div>
                <div class="text-body-2">
                  Completed Value
                </div>
              </div>
            </div>
          </VCol>

          <VDivider
            v-if="isAdmin"
            vertical
            class="mx-2"
          />

          <VCol
            v-if="isAdmin"
            cols="auto"
          >
            <div class="d-flex align-center">
              <VAvatar
                variant="tonal"
                rounded
                size="32"
                color="primary"
                class="me-2"
              >
                <VIcon
                  size="20"
                  icon="tabler-shopping-cart"
                />
              </VAvatar>
              <div>
                <div class="text-h6">
                  {{ totalOrdersNumber }}
                </div>
                <div class="text-body-2">
                  Total Orders
                </div>
              </div>
            </div>
          </VCol>

          <VDivider
            vertical
            class="mx-2"
          />

          <VCol
            cols="auto"
            class="ms-auto"
          >
            <VBtn
              v-if="isAdmin"
              color="primary"
              prepend-icon="tabler-truck-delivery"
              @click="
                router.push(
                  '/apps/ecommerce/logistics/actions/orderDrivingActions',
                )
              "
            >
              Manage Driving Actions
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!--
      Plan CTA: opens the dedicated /plan page with an optional pre-filled destination and
      date. Admin-only, because /plan is - a driver pressing this was sent to the page and
      bounced straight to not-authorized by the router guard. Planning the day's driving is
      dispatch, not driving.
    -->
    <VCard
      v-if="isAdmin"
      class="mb-5"
    >
      <VCardTitle
        class="font-weight-medium pb-3 d-flex align-center cursor-pointer"
        role="button"
        @click="filtersExpanded = !filtersExpanded"
      >
        <span>{{ $t("orders.filters.title") }}</span>
        <VIcon
          class="ms-2"
          size="20"
          :icon="filtersExpanded ? 'tabler-chevron-up' : 'tabler-chevron-down'"
        />
      </VCardTitle>
      <VCardText
        v-show="filtersExpanded"
        class="pt-4"
      >
        <VRow>
          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <CustomDropdownFilter
              label="Driving Destination"
              :model-value="selectedPlanningDestination"
              :items="planningDestinationOptions"
              :multiple="false"
              @update:model-value="selectedPlanningDestination = $event"
            />
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <CustomDatePicker
              label="Due Date"
              :model-value="[selectedPlanningDate, selectedPlanningDate]"
              :range="false"
              @update:model-value="
                (val) =>
                  (selectedPlanningDate
                    = Array.isArray(val) && val[0] ? val[0] : null)
              "
              @clear="selectedPlanningDate = null"
            />
          </VCol>

          <VCol
            cols="12"
            sm="12"
            md="4"
            class="d-flex align-center"
          >
            <VBtn
              color="primary"
              prepend-icon="tabler-calendar-plus"
              @click="onPlanCta"
            >
              {{ $t("orders.plan.applyCta") }}
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Global success message -->
    <VAlert
      v-if="globalSuccessMessage"
      type="success"
      variant="tonal"
      closable
      class="mb-4"
      @click:close="globalSuccessMessage = null"
    >
      {{ globalSuccessMessage }}
    </VAlert>

    <!-- Loading indicator when refreshing orders -->
    <VAlert
      v-if="isLoadingOrders"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      <VProgressCircular
        indeterminate
        class="me-3"
      />
      Refreshing orders...
    </VAlert>

    <!--
      The table's own search filters only the rows already loaded into the current tab, so a code
      a customer reads out cannot be found if their order sits on another tab or page. This asks
      the server and jumps straight to the order.
    -->
    <VCard
      v-if="isAdmin"
      class="mb-4"
    >
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <VTextField
          v-model="lookupCode"
          label="Find order by code"
          placeholder="A2BC"
          density="compact"
          hide-details
          style="max-inline-size: 220px"
          :error-messages="lookupError ? [lookupError] : []"
          @keyup.enter="findOrderByCode"
        />
        <VBtn
          :loading="lookupLoading"
          :disabled="!lookupCode"
          prepend-icon="tabler-search"
          @click="findOrderByCode"
        >
          Find
        </VBtn>
      </VCardText>
    </VCard>

    <VCard>
      <VTabs v-model="currentTab">
        <VTab>
          <VAvatar
            variant="tonal"
            rounded
            size="24"
            color="primary"
            class="me-2"
          >
            <VIcon
              size="16"
              icon="tabler-progress"
              color="primary"
            />
          </VAvatar>
          Active Orders
        </VTab>
        <VTab>
          <VAvatar
            variant="tonal"
            rounded
            size="24"
            color="grey"
            class="me-2"
          >
            <span class="text-grey-darken-2 font-weight-medium text-xs">⏱️</span>
          </VAvatar>
          On Hold Orders
        </VTab>
        <!--
          Completed and Canceled are admin-only, and empty for a driver in any case:
          assignment clears the moment the goods leave their hands, so a finished order
          stops being theirs at the instant it finishes. A tab that can only ever be blank
          is a question a driver would keep asking.
        -->
        <VTab v-if="isAdmin">
          <VAvatar
            variant="tonal"
            rounded
            size="24"
            color="success"
            class="me-2"
          >
            <span class="text-success-darken-1 font-weight-medium text-xs">✓</span>
          </VAvatar>
          Completed Orders
        </VTab>
        <VTab v-if="isAdmin">
          <VAvatar
            variant="tonal"
            rounded
            size="24"
            color="error"
            class="me-2"
          >
            <span class="text-error-darken-1 font-weight-medium text-xs">X</span>
          </VAvatar>
          Canceled Orders
        </VTab>
      </VTabs>

      <VDivider />

      <VWindow v-model="currentTab">
        <VWindowItem :value="0">
          <div
            v-if="!isMobile"
            class="d-flex align-center justify-end px-4 pt-3"
          >
            <VSwitch
              v-model="drivingView"
              :label="
                drivingView
                  ? $t('orders.activeViews.driving')
                  : $t('orders.activeViews.default')
              "
              color="primary"
              hide-details
              density="compact"
            />
          </div>
          <div
            v-if="isMobile"
            class="px-3 pt-3"
          >
            <MobileOrdersViewToggle v-model="mobileOrdersView" />
          </div>
          <DrivingDayBoard
            v-if="isMobile ? mobileOrdersView === 'driving' : drivingView"
            :orders="activeOrders"
            :grid="!isMobile"
            @refresh="refreshOrders"
          />
          <CustomDataTable
            v-else
            :items="activeOrders"
            :columns="activeColumns"
            :initial-sort-by="{ key: 'nextDrivingAction.dueIn', order: 'asc' }"
          />
        </VWindowItem>

        <VWindowItem :value="1">
          <!--
            FIX: The onHoldOrders table was empty because the CustomDataTable
            was not getting a new array reference when the data changed.
            We force a new array reference by using [...onHoldOrders.value].
            Also, ensure :key is set for each row (handled by CustomDataTable).
          -->
          <CustomDataTable
            :items="[...onHoldOrders]"
            :columns="onHoldColumns"
            :initial-sort-by="{ key: 'nextDrivingAction.dueIn', order: 'asc' }"
            :actions="
              (item: ProcessedOrder) =>
                isAdmin
                  ? [
                    {
                      icon: 'tabler-calendar-event',
                      label: 'Resolve and Reschedule',
                      color: 'primary',
                      onClick: () => showResolveDialog(item),
                    },
                  ]
                  : []
            "
          />
        </VWindowItem>

        <VWindowItem
          v-if="isAdmin"
          :value="2"
        >
          <CustomDataTable
            :items="completedOrders"
            :columns="completedColumns"
            :initial-sort-by="{ key: 'currentStepLastUpdate', order: 'desc' }"
          />
        </VWindowItem>

        <VWindowItem
          v-if="isAdmin"
          :value="3"
        >
          <CustomDataTable
            :items="canceledOrders"
            :columns="canceledColumns"
            :initial-sort-by="{ key: 'currentStepLastUpdate', order: 'desc' }"
          />
        </VWindowItem>
      </VWindow>
    </VCard>

    <!-- Resolve Order Dialog -->
    <VDialog
      v-model="isResolveDialogOpen"
      max-width="600"
    >
      <VCard>
        <VCardTitle>
          {{
            selectedOrder?.orderStatus === OrderStatusEnum.ON_HOLD
              ? "Resolve and Reschedule Order"
              : "Reschedule Order"
          }}
        </VCardTitle>
        <VCardText v-if="!selectedOrder">
          <VAlert type="error">
            Error: Unable to load order details. Please try again.
          </VAlert>
        </VCardText>
        <VCardText v-else>
          <div class="mb-4">
            <p class="text-h6">
              Updating order <strong>#{{ selectedOrder.id }}</strong>
            </p>
            <p v-if="selectedOrder.customer">
              for {{ selectedOrder.customer.name }}
            </p>
            <p class="text-h5 mt-1 mb-2">
              {{
                getActionDisplayName(
                  selectedOrder.nextDrivingAction?.type || "",
                )
              }}
            </p>
          </div>

          <!-- STORE_PICKUP / STORE_DELIVERY -->
          <VRow v-if="isStoreAction">
            <VCol cols="12">
              <CustomDatePicker
                label="Due Date"
                :model-value="[
                  nextDrivingActionDueDate,
                  nextDrivingActionDueDate,
                ]"
                :range="false"
                @update:model-value="
                  (val) =>
                    (nextDrivingActionDueDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
            </VCol>
            <VCol
              v-if="canModifyPickup"
              cols="12"
            >
              <VCheckbox
                v-model="modifyPickup"
                label="Modify Customer Pickup Time"
              />
            </VCol>
            <VCol
              v-if="modifyPickup"
              cols="12"
            >
              <CustomDatePicker
                label="Pickup Date"
                :model-value="[pickupDate, pickupDate]"
                :range="false"
                :min-date="minDateForPicker"
                @update:model-value="
                  (val) =>
                    (pickupDate = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Pickup Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedPickupTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in pickupTimeWindows"
                  :key="`pickup-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, true) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, true) ? 'elevated' : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, true)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, true)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="pickupTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
            <VCol cols="12">
              <VCheckbox
                v-model="modifyDelivery"
                label="Modify Customer Delivery Time"
              />
            </VCol>
            <VCol
              v-if="modifyDelivery"
              cols="12"
            >
              <CustomDatePicker
                label="Delivery Date"
                :model-value="[deliveryDate, deliveryDate]"
                :range="false"
                :min-date="minDeliveryDate"
                @update:model-value="
                  (val) =>
                    (deliveryDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Delivery Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedDeliveryTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in deliveryTimeWindows"
                  :key="`delivery-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, false) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, false)
                      ? 'elevated'
                      : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, false)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, false)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="deliveryTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
          </VRow>

          <!-- CUSTOMER_PICKUP -->
          <VRow v-else-if="isCustomerPickup">
            <VCol cols="12">
              <CustomDatePicker
                label="Pickup Date"
                :model-value="[pickupDate, pickupDate]"
                :range="false"
                :min-date="minDateForPicker"
                @update:model-value="
                  (val) =>
                    (pickupDate = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Pickup Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedPickupTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in pickupTimeWindows"
                  :key="`pickup-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, true) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, true) ? 'elevated' : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, true)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, true)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="pickupTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
            <VCol
              v-if="canModifyDelivery"
              cols="12"
            >
              <VCheckbox
                v-model="modifyDelivery"
                label="Modify Customer Delivery Time"
              />
            </VCol>
            <VCol
              v-if="modifyDelivery"
              cols="12"
            >
              <CustomDatePicker
                label="Delivery Date"
                :model-value="[deliveryDate, deliveryDate]"
                :range="false"
                :min-date="minDeliveryDate"
                @update:model-value="
                  (val) =>
                    (deliveryDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Delivery Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedDeliveryTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in deliveryTimeWindows"
                  :key="`delivery-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, false) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, false)
                      ? 'elevated'
                      : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, false)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, false)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="deliveryTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
          </VRow>

          <!-- CUSTOMER_DELIVERY -->
          <VRow v-else-if="isCustomerDelivery">
            <VCol cols="12">
              <CustomDatePicker
                label="Delivery Date"
                :model-value="[deliveryDate, deliveryDate]"
                :range="false"
                :min-date="minDeliveryDate"
                @update:model-value="
                  (val) =>
                    (deliveryDate
                      = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Delivery Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedDeliveryTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in deliveryTimeWindows"
                  :key="`delivery-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, false) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, false)
                      ? 'elevated'
                      : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, false)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, false)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="deliveryTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
            <VCol
              v-if="canModifyPickup"
              cols="12"
            >
              <VCheckbox
                v-model="modifyPickup"
                label="Modify Customer Pickup Time"
              />
            </VCol>
            <VCol
              v-if="modifyPickup"
              cols="12"
            >
              <CustomDatePicker
                label="Pickup Date"
                :model-value="[pickupDate, pickupDate]"
                :range="false"
                :min-date="minDateForPicker"
                @update:model-value="
                  (val) =>
                    (pickupDate = Array.isArray(val) && val[0] ? val[0] : null)
                "
              />
              <div class="d-flex align-center mb-2 mt-2">
                <div class="text-subtitle-1">
                  Pickup Time Windows
                </div>
                <div class="text-caption ms-2">
                  (Selected: {{ selectedPickupTimeWindows.length }})
                </div>
              </div>
              <div class="mb-2 text-caption">
                Tap on a time window to select/deselect it
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="window in pickupTimeWindows"
                  :key="`pickup-${window.startTime}-${window.endTime}`"
                  :color="
                    isTimeWindowSelected(window, true) ? 'primary' : undefined
                  "
                  :variant="
                    isTimeWindowSelected(window, true) ? 'elevated' : 'outlined'
                  "
                  size="large"
                  class="ma-1"
                  @click="toggleTimeWindow(window, true)"
                >
                  <template
                    v-if="isTimeWindowSelected(window, true)"
                    #prepend
                  >
                    <VIcon
                      icon="tabler-check"
                      size="small"
                    />
                  </template>
                  {{ formatTimeWindow(window) }}
                </VChip>
              </div>
              <div
                v-if="pickupTimeWindows.length === 0"
                class="text-caption text-grey"
              >
                No time windows available
              </div>
            </VCol>
          </VRow>

          <VAlert
            v-if="resolveMessage"
            :type="resolveMessage.type"
            class="mt-4"
            closable
          >
            {{ resolveMessage.text }}
          </VAlert>
        </VCardText>

        <VCardActions>
          <VBtn
            color="secondary"
            variant="outlined"
            :disabled="isResolving"
            @click="closeResolveDialog"
          >
            Cancel
          </VBtn>
          <VSpacer />
          <VBtn
            color="primary"
            variant="elevated"
            :disabled="isResolveButtonDisabled"
            :loading="isResolving"
            size="large"
            @click="resolveOrder"
          >
            <VIcon
              start
              icon="tabler-calendar-event"
            />
            {{
              selectedOrder?.orderStatus === OrderStatusEnum.ON_HOLD
                ? "Resolve and Reschedule"
                : "Reschedule"
            }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.customer-title:hover {
  color: rgba(var(--v-theme-primary)) !important;
}

.product-widget {
  border-block-end: 1px solid
    rgba(var(--v-theme-on-surface), var(--v-border-opacity));
  padding-block-end: 1rem;
}
@keyframes pulse-warning {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 159, 67, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 159, 67, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 159, 67, 0);
  }
}

.outsource-warning-badge {
  display: inline-flex;
  align-items: center;
  background-color: #fff3e0;
  border: 1px solid #ff9f43;
  color: #e65100;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  margin-left: 8px;
  position: relative;
  cursor: help;
  animation: pulse-warning 2s infinite;
  white-space: nowrap;
  vertical-align: middle;

  &::after {
    content: "Outsource is still in progress. Contact Outsource company to submit items";
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: normal;
    white-space: normal;
    width: 200px;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s,
      visibility 0.2s;
    z-index: 10;
    pointer-events: none;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    text-align: center;
  }

  &::before {
    content: "";
    position: absolute;
    bottom: 110%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: #333 transparent transparent transparent;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s,
      visibility 0.2s;
    z-index: 10;
  }

  &:hover::after,
  &:hover::before {
    opacity: 1;
    visibility: visible;
  }
}

.outsource-warning-badge i {
  font-size: 14px;
  margin-right: 4px;
}
</style>
